import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import {
  ShoppingCart,
  ArrowForward,
  RemoveShoppingCart,
  LockOutlined,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CartItem from '../../components/storefront/cart/CartItem';
import CouponInput from '../../components/storefront/cart/CouponInput';
import CartSavedIndicator from '../../components/storefront/cart/CartSavedIndicator';
import { useGetCart } from '../../hooks/storefront/useGetCart';
import { useValidateCart } from '../../hooks/storefront/useValidateCart';
import { useGetStorefrontSettings } from '../../hooks/storefront/useGetStorefrontSettings';
import { useAuthContext } from '../../auth/useAuthContext';
import { formatMoney } from '../../utils/money';

const SummaryRow = ({ label, value, valueColor, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
    <Typography variant="body2" color={bold ? 'text.primary' : 'text.secondary'} fontWeight={bold ? 700 : 400}>
      {label}
    </Typography>
    <Typography variant="body2" color={valueColor || 'text.primary'} fontWeight={bold ? 700 : 500}>
      {value}
    </Typography>
  </Box>
);

const Cart = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { cartData: cart, isLoading, isFetching, error, dataUpdatedAt } = useGetCart();
  const { isAuthenticated } = useAuthContext();
  const { allowGuestCheckout } = useGetStorefrontSettings();

  const items = cart?.items || [];
  const subtotal = Number(cart?.subtotal) || 0;
  const tax = Number(cart?.tax_total ?? cart?.tax) || 0;
  const shipping = Number(cart?.shipping_total ?? cart?.shipping) || 0;
  const discount = Number(cart?.discount_total ?? cart?.discount) || 0;
  const currency = cart?.currency;

  // Stock validation — fired on every cart load. Issues come back per
  // line so the UI can disable Checkout when something is wrong.
  const { data: validation } = useValidateCart();
  const validationData = validation?.data?.data || validation?.data || {};
  const validationIssuesById = (validationData.items || validationData.issues || [])
    .reduce((acc, row) => {
      const id = row.cart_item_id ?? row.item_id ?? row.id;
      if (id != null && row && (row.available != null || row.error || row.message)) {
        acc[id] = row;
      }
      return acc;
    }, {});
  const hasBlockingIssue = Object.values(validationIssuesById).some(
    (row) => row.available === 0 || row.blocking || row.severity === 'error'
  );
  const appliedCoupon = cart?.coupon || null;

  const apiTotal = Number(cart?.grand_total ?? cart?.total);
  const total = Number.isFinite(apiTotal) && apiTotal > 0
    ? apiTotal
    : Math.max(0, subtotal + tax + shipping - discount);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {t('cart.error', 'Failed to load cart. Please try again.')}
        </Alert>
      </Container>
    );
  }

  if (items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', borderRadius: 3 }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <RemoveShoppingCart sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h5" gutterBottom fontWeight={700}>
            {t('cart.empty', 'Your cart is empty')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
            {t('cart.emptyMessage', 'Add some products to your cart to see them here.')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/')}
            startIcon={<ShoppingCart />}
            sx={{ borderRadius: 2, px: 4 }}
          >
            {t('cart.continueShopping', 'Continue Shopping')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 3, md: 4 } }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            {t('cart.title', 'Shopping Cart')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Chip
              size="small"
              label={t('cart.itemCount', '{{count}} items in your cart', { count: items.length })}
              sx={{ fontWeight: 600 }}
            />
            <CartSavedIndicator updatedAt={dataUpdatedAt} isFetching={isFetching && !isLoading} />
          </Stack>
        </Box>
        <Button
          variant="text"
          startIcon={isRTL ? <ArrowForward /> : <ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('cart.continueShopping', 'Continue Shopping')}
        </Button>
      </Stack>

      <Grid container spacing={{ xs: 3, md: 4 }}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          <Box>
            {items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                currency={currency}
                stockIssue={validationIssuesById[item.id]}
              />
            ))}
          </Box>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {/* Coupon */}
            <CouponInput appliedCoupon={appliedCoupon} discount={discount} subtotal={subtotal} currency={currency} />

            {/* Summary */}
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                {t('cart.summary', 'Order Summary')}
              </Typography>

              <SummaryRow
                label={t('cart.subtotal', 'Subtotal')}
                value={formatMoney(subtotal, currency)}
              />

              {tax > 0 && (
                <SummaryRow
                  label={t('cart.tax', 'Tax')}
                  value={formatMoney(tax, currency)}
                />
              )}

              <SummaryRow
                label={t('cart.shipping', 'Shipping')}
                value={shipping > 0 ? formatMoney(shipping, currency) : t('common.free', 'Free')}
                valueColor={shipping > 0 ? undefined : 'success.main'}
              />

              {discount > 0 && (
                <SummaryRow
                  label={
                    appliedCoupon?.code
                      ? `${t('cart.discount', 'Discount')} (${appliedCoupon.code})`
                      : t('cart.discount', 'Discount')
                  }
                  value={`− ${formatMoney(discount, currency)}`}
                  valueColor="success.main"
                />
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('cart.total', 'Total')}
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight={800}>
                  {formatMoney(total, currency)}
                </Typography>
              </Box>

              {hasBlockingIssue && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t(
                    'cart.stockBlocking',
                    'Some items have stock issues. Update quantities or remove them to continue.'
                  )}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate(isAuthenticated ? '/checkout' : '/login?returnTo=/checkout')}
                disabled={hasBlockingIssue}
                endIcon={isRTL ? null : <ArrowForward />}
                startIcon={isRTL ? <ArrowForward sx={{ transform: 'scaleX(-1)' }} /> : null}
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 'none',
                }}
              >
                {isAuthenticated
                  ? t('cart.proceedToCheckout', 'Proceed to Checkout')
                  : t('cart.signInToCheckout', 'Sign in to checkout')}
              </Button>

              {!isAuthenticated && allowGuestCheckout && (
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  onClick={() => navigate('/checkout')}
                  disabled={hasBlockingIssue}
                  sx={{
                    mt: 1.25,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  {t('cart.continueAsGuest', 'Continue as guest')}
                </Button>
              )}

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 2, color: 'text.secondary' }}
              >
                <LockOutlined sx={{ fontSize: 14 }} />
                <Typography variant="caption">
                  {t('cart.secureCheckout', 'Secure checkout')}
                </Typography>
              </Stack>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Cart;
